import {Injectable} from '@angular/core';
import * as moment from 'moment';

@Injectable({
    providedIn: 'root',
})
export class UtilsService {
    private i18nCalendar: any;

    constructor() {
        this.init();
    }

    getI18nCalendar(lang: string) {
        return this.i18nCalendar[lang];
    }

    slugifyText(input: string): string {
        if (input) {
            const trChars = {
                çÇ: 'c',
                ğĞ: 'g',
                şŞ: 's',
                üÜ: 'u',
                ıİ: 'i',
                öÖ: 'o',
            };
            for (const key of Object.keys(trChars)) {
                if (input) {
                    input = input.replace(new RegExp('[' + key + ']', 'g'), trChars[key]);
                }
            }
            return input
                .toString()
                .toLowerCase()
                .replace(/\s+/g, '-') // Replace spaces with -
                .replace(/[^\w\-]+/g, '') // Remove all non-word chars
                .replace(/\-\-+/g, '-') // Replace multiple - with single -
                .replace(/^-+/, '') // Trim - from start of text
                .replace(/-+$/, ''); // Trim - from end of text
        } else {
            return input;
        }
    }

    /**
     * Normalize a timestamp that can be in seconds or milliseconds (or already a Date/ISO string)
     * to a proper Date instance using moment.js.
     * 
     * Uses moment.js to automatically detect and handle both Unix timestamps (seconds) 
     * and JavaScript timestamps (milliseconds).
     * 
     * @param value - Can be: Date object, number (seconds or milliseconds), ISO string, or null/undefined
     * @returns Date object or null if value is falsy
     */
    normalizeTimestampToDate(value: any): Date | null {
        if (!value) {
            return null;
        }

        // Already a Date object
        if (value instanceof Date) {
            return value;
        }

        // If it's a number, try both Unix timestamp (seconds) and milliseconds
        if (typeof value === 'number') {
            // Try moment.unix() first (for Unix timestamps in seconds)
            const unixMoment = moment.unix(value);
            if (unixMoment.isValid()) {
                return unixMoment.toDate();
            }

            // If unix() didn't work, try regular moment() for milliseconds
            const msMoment = moment(value);
            if (msMoment.isValid()) {
                return msMoment.toDate();
            }
        }

        // For strings (ISO format, etc.), use moment's parsing
        const parsedMoment = moment(value);
        if (parsedMoment.isValid()) {
            return parsedMoment.toDate();
        }

        // Fallback: let Date try to parse the value
        return new Date(value);
    }

    private init() {
        this.i18nCalendar = {
            en: {
                firstDayOfWeek: 0,
                dayNames: [
                    'Sunday',
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                ],
                dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                monthNames: [
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December',
                ],
                monthNamesShort: [
                    'Jan',
                    'Feb',
                    'Mar',
                    'Apr',
                    'May',
                    'Jun',
                    'Jul',
                    'Aug',
                    'Sep',
                    'Oct',
                    'Nov',
                    'Dec',
                ],
                today: 'Today',
                clear: 'Clear',
                dateFormat: 'mm/dd/yy',
                weekHeader: 'Wk',
            },
            fr: {
                firstDayOfWeek: 1,
                dayNames: [
                    'Dimanche',
                    'Lundi',
                    'Mardi',
                    'Mercredi',
                    'Jeudi',
                    'Vendredi',
                    'Samedi',
                ],
                dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
                dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
                monthNames: [
                    'Janvier',
                    'Février',
                    'Mars',
                    'Avril',
                    'Mai',
                    'Juin',
                    'Juillet',
                    'Août',
                    'Septembre',
                    'Octobre',
                    'Novembre',
                    'Décembre',
                ],
                monthNamesShort: [
                    'Jan',
                    'Feb',
                    'Mar',
                    'Avr',
                    'Mai',
                    'Juin',
                    'Juil',
                    'Aou',
                    'Sep',
                    'Oct',
                    'Nov',
                    'Dec',
                ],
                today: 'Aujourd\'hui',
                clear: 'Vider',
                dateFormat: 'mm/dd/yy',
                weekHeader: 'S',
            },
        };
    }
}
